import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getCollection,
  getId,
  getSiteLabel,
  requestJson,
  stripEmptyValues,
} from '../lib/api';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import { useMenuPage } from '../hooks/useMenuPage';

const emptyForm = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  siteId: '',
};

function matchesFilter(value, filter) {
  return String(value || '').toLowerCase().includes(filter.trim().toLowerCase());
}

export default function CustomersPage() {
  const [searchParams] = useSearchParams();
  const menuPage = useMenuPage('/clients');
  const [rows, setRows] = useState([]);
  const [sites, setSites] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [open, setOpen] = useState(false);
  const [quickFilter, setQuickFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState({
    lastName: '',
    firstName: '',
    fullName: '',
    phone: '',
    email: '',
    site: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const payload = await requestJson('/customers');
      setRows(getCollection(payload));
    } catch (err) {
      setError(err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSites = useCallback(async () => {
    try {
      const payload = await requestJson('/sites');
      setSites(getCollection(payload));
    } catch (_err) {
      setSites([]);
    }
  }, []);

  useEffect(() => {
    loadRows();
    loadSites();
  }, [loadRows, loadSites]);

  useEffect(() => {
    const q = searchParams.get('q') || '';
    if (q) setQuickFilter(q);
  }, [searchParams]);

  const gridRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        id: row._id,
        fullName: [row.firstName, row.lastName].filter(Boolean).join(' '),
        siteLabel: getSiteLabel(row.siteId),
      })),
    [rows]
  );

  const filterOptions = useMemo(() => {
    const collect = (selector) => [...new Set(gridRows.map(selector).filter(Boolean))].sort();

    return {
      lastNames: collect((row) => row.lastName),
      firstNames: collect((row) => row.firstName),
      fullNames: collect((row) => row.fullName),
      phones: collect((row) => row.phone),
      emails: collect((row) => row.email),
      sites: collect((row) => row.siteLabel),
    };
  }, [gridRows]);

  const filteredGridRows = useMemo(() => {
    const normalizedQuickFilter = quickFilter.trim().toLowerCase();

    return gridRows.filter(
      (row) =>
        (!normalizedQuickFilter ||
          [row.lastName, row.firstName, row.fullName, row.phone, row.email, row.siteLabel]
            .join(' ')
            .toLowerCase()
            .includes(normalizedQuickFilter)) &&
        matchesFilter(row.lastName, columnFilters.lastName) &&
        matchesFilter(row.firstName, columnFilters.firstName) &&
        matchesFilter(row.fullName, columnFilters.fullName) &&
        matchesFilter(row.phone, columnFilters.phone) &&
        matchesFilter(row.email, columnFilters.email) &&
        matchesFilter(row.siteLabel, columnFilters.site)
    );
  }, [gridRows, quickFilter, columnFilters]);

  const resetFilters = () => {
    setQuickFilter('');
    setColumnFilters({
      lastName: '',
      firstName: '',
      fullName: '',
      phone: '',
      email: '',
      site: '',
    });
  };

  const filterFields = [
    ['lastName', 'Nom', filterOptions.lastNames],
    ['firstName', 'Prénom', filterOptions.firstNames],
    ['fullName', 'Client', filterOptions.fullNames],
    ['phone', 'Téléphone', filterOptions.phones],
    ['email', 'Email', filterOptions.emails],
    ['site', 'Site', filterOptions.sites],
  ];

  const handleOpenCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setOpen(true);
  };

  const handleOpenEdit = useCallback((row) => {
    setForm({
      firstName: row.firstName || '',
      lastName: row.lastName || '',
      phone: row.phone || '',
      email: row.email || '',
      siteId: getId(row.siteId),
    });
    setEditingId(row._id);
    setOpen(true);
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      setError('');
      await requestJson(editingId ? `/customers/${editingId}` : '/customers', {
        method: editingId ? 'PUT' : 'POST',
        body: JSON.stringify(stripEmptyValues(form)),
      });
      setOpen(false);
      await loadRows();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = useCallback(
    async (id) => {
      try {
        setError('');
        await requestJson(`/customers/${id}`, { method: 'DELETE' });
        setDeleteTarget(null);
        await loadRows();
      } catch (err) {
        setError(err.message);
      }
    },
    [loadRows]
  );

  const columns = useMemo(
    () => [
      { field: 'lastName', headerName: 'Nom', minWidth: 160, flex: 0.9 },
      { field: 'firstName', headerName: 'Prénom', minWidth: 150, flex: 0.8 },
      { field: 'fullName', headerName: 'Client', minWidth: 220, flex: 1 },
      { field: 'phone', headerName: 'Téléphone', minWidth: 150, flex: 0.8 },
      { field: 'email', headerName: 'Email', minWidth: 220, flex: 1 },
      { field: 'siteLabel', headerName: 'Site', minWidth: 200, flex: 1 },
      {
        field: 'actions',
        headerName: 'Action',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'right',
        headerAlign: 'right',
        minWidth: 120,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ width: '100%' }}>
            <IconButton size="small" onClick={() => handleOpenEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" color="error" onClick={() => setDeleteTarget(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [handleOpenEdit]
  );

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="overline" color="primary" sx={{ fontWeight: 850 }}>
          {menuPage.sectionTitle}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 900 }}>
          {menuPage.pageTitle}
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', borderRadius: 2 }}>
        <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
          <Stack spacing={1.25}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FilterAltRoundedIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                  Filtres
                </Typography>
              </Stack>
              <Button size="small" variant="outlined" onClick={resetFilters} sx={{ minHeight: 32 }}>
                Réinitialiser
              </Button>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(auto-fit, minmax(150px, 1fr))',
                },
                gap: 1.5,
              }}
            >
              <TextField
                size="small"
                label="Recherche globale"
                value={quickFilter}
                onChange={(event) => setQuickFilter(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                fullWidth
              />
              {filterFields.map(([key, label, options]) => (
                <Autocomplete
                  key={key}
                  freeSolo
                  size="small"
                  options={options}
                  value={columnFilters[key] || ''}
                  onInputChange={(_event, value) =>
                    setColumnFilters((current) => ({ ...current, [key]: value || '' }))
                  }
                  onChange={(_event, value) =>
                    setColumnFilters((current) => ({ ...current, [key]: value || '' }))
                  }
                  renderInput={(params) => <TextField {...params} label={label} />}
                  fullWidth
                />
              ))}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ alignSelf: 'center' }}>
            {filteredGridRows.length} client{filteredGridRows.length > 1 ? 's' : ''}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadRows}>
              Rafraîchir
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
              Ajouter
            </Button>
          </Stack>
        </Stack>

        {loading ? (
          <Stack alignItems="center" sx={{ py: 8 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <Box sx={{ height: 620, width: '100%' }}>
            <DataGrid
              rows={filteredGridRows}
              columns={columns}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              pageSizeOptions={[10, 25, 50, 100]}
              disableRowSelectionOnClick
              sx={{ border: 0 }}
            />
          </Box>
        )}
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={handleSubmit}>
          <DialogTitle>{editingId ? 'Modifier un client' : 'Ajouter un client'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField name="firstName" label="Prénom" value={form.firstName} onChange={handleChange} fullWidth />
                <TextField name="lastName" label="Nom" value={form.lastName} onChange={handleChange} required fullWidth />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField name="phone" label="Téléphone" value={form.phone} onChange={handleChange} fullWidth />
                <TextField name="email" label="Email" type="email" value={form.email} onChange={handleChange} fullWidth />
              </Stack>
              <TextField name="siteId" label="Site d'origine" select value={form.siteId} onChange={handleChange}>
                <MenuItem value="">Aucun site</MenuItem>
                {sites.map((site) => (
                  <MenuItem key={site._id} value={site._id}>
                    {getSiteLabel(site)}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Annuler</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <ConfirmDeleteDialog
        open={Boolean(deleteTarget)}
        itemLabel={`le client ${deleteTarget?.fullName || deleteTarget?.lastName || ''}`}
        description={`Confirmer la suppression du client ${
          deleteTarget?.fullName || deleteTarget?.lastName || ''
        } ?`}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => handleDelete(deleteTarget._id)}
      />
    </Stack>
  );
}
